import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { goEnquire } from "../../lib/scroll";

const LINES = ["The Right Finance.", "The Right Strategy.", "The Right Partner."];

const STATS = [
  { value: "13+", label: "Financing & Advisory Solutions" },
  { value: "8", label: "Lender Categories in Network" },
  { value: "9+", label: "Industries Served" },
  { value: "5.0", label: "Rated on Google Reviews", stars: true },
];

export const Hero = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 180]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section className="noise-overlay relative flex min-h-[96vh] flex-col justify-end overflow-hidden bg-navy-deep" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src="/assets/hero-finance.jpeg" alt="Golden growth curve rising over a financial district skyline" className="h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/90 via-navy-deep/50 to-navy-deep/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/85 via-transparent to-navy-deep/30" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pt-40 md:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="flex items-center gap-4 font-mono text-xs uppercase tracking-[0.35em] text-gold"
          data-testid="hero-overline"
        >
          <span className="h-px w-10 bg-gold/70" />
          Your Preferred Lending Partner
        </motion.p>

        <h1 className="mt-9 font-heading text-[11.5vw] font-bold leading-[1.04] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-[74px]" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 2 ? (
                  <>
                    The Right{" "}
                    <span className="bg-gradient-to-r from-[#E8CD6E] via-gold to-[#B8912B] bg-clip-text text-transparent">Partner.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/80 md:text-lg"
          data-testid="hero-subheadline"
        >
          LendX is a financial advisory firm helping individuals and businesses structure loans, resolve debt, and access the right funding — with discreet, expert guidance at every step.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => navigate("/solutions")}
            data-testid="hero-cta-solutions"
            className="group flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E3C558] via-gold to-[#B8912B] px-8 py-4 text-sm font-bold text-navy-deep shadow-[0_10px_30px_rgba(212,175,55,0.3)] transition-transform duration-300 hover:scale-[1.04]"
          >
            Explore Loan Solutions
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={() => goEnquire(navigate)}
            data-testid="hero-cta-expert"
            className="rounded-full border border-gold/50 px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            Talk to an Expert
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-16 grid grid-cols-2 gap-x-6 gap-y-8 border-t border-gold/20 py-10 md:grid-cols-4"
          data-testid="hero-stats"
        >
          {STATS.map((s, i) => (
            <div key={s.label} className="border-l border-gold/30 pl-5" data-testid={`hero-stat-${i + 1}`}>
              <p className="flex items-center gap-2 font-heading text-3xl font-bold tracking-tight text-gold md:text-4xl">
                {s.value}
                {s.stars && <Star size={18} className="fill-gold text-gold" />}
              </p>
              <p className="mt-2 text-xs font-medium uppercase tracking-wide text-white/60 md:text-[13px]">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-8 hidden items-center gap-3 text-white/60 lg:flex"
        data-testid="hero-scroll-indicator"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={14} className="animate-bounce text-gold" />
      </motion.div>
    </section>
  );
};
